import useCMS from '@telescope/react-hooks/useCMS';
import { css } from 'aphrodite/no-important';
import { SharedCarousel } from 'components';
import { AgreementForm, RatingForm } from 'components/SurveyElements';
import { CommentForm } from 'components/SurveyElements/CommentForm/CommentForm';
import { useScrollToTopOnMount } from 'hooks';
import useSurvey from 'hooks/useSurvey';
import { isEmpty } from 'lodash';
import { CmsData, UserData } from 'models';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Prompt, useHistory } from 'react-router';
import { RootState } from 'redux/store';
import { MIN_POLLING_RATE } from 'util/constants';
import { getQueryParamByName } from 'util/helpers';
import { hasSurveyBeenSubmittedByUser, setSurveyHasBeenSubmittedByUser } from 'util/survey-helpers';
import { style } from './style';

export const Survey: FC = () => {
  useScrollToTopOnMount();

  const styles = style();
  const { submitSurvey, getSurvey } = useSurvey();
  const history = useHistory();
  const userData = useSelector((state: RootState) => state.user.data);
  const cmsData = useSelector((state: RootState) => state.widget.cms.text.survey);
  const surveyWid = useSelector((state: RootState) => state.widget.cms.settings.survey_wid);
  const surveyPollingRate = useSelector(
    (state: RootState) => state.widget.cms.text.settings.survey_wid_polling_rate_ms
  );

  const wid = getQueryParamByName('s_wid') || surveyWid;
  const sid = getQueryParamByName('s_sid');

  const [surveyData] = useCMS<CmsData>(wid, { sid, pollingFrequency: surveyPollingRate || MIN_POLLING_RATE });

  const [loading, setLoading] = useState(true);
  const [hasSurveyBeenSubmitted, setSurveyHasBeenSubmitted] = useState(() => {
    return !!userData && hasSurveyBeenSubmittedByUser(userData);
  });

  const { register, handleSubmit, errors } = useForm();

  useEffect(() => {
    // Prevent cms injected links from redirecting as an external link if the route is inside the app
    const cmsLinks = document.getElementsByClassName('cms_link');
    Array.prototype.forEach.call(cmsLinks, (element: Element) => {
      const route = element.getAttribute('href');
      if (route && route.trim().charAt(0) === '/') {
        element.addEventListener('click', (event) => {
          event.preventDefault();
          history.push(route);
        });
      }
    });
  });

  // API:: Initial fetched data
  useEffect(() => {
    getSurvey().then((response) => {
      try {
        const survey = JSON.parse(response.survey);
        if (userData && survey.total > 0) {
          setUserHasSubmittedFlag(userData, true);
        }
      } catch {
      } finally {
        setLoading(false);
      }
    });
  }, [getSurvey, userData]);

  const setUserHasSubmittedFlag = (userData: UserData | null, submitted: boolean) => {
    if (!userData) return;
    setSurveyHasBeenSubmittedByUser(userData);
    setSurveyHasBeenSubmitted(true);
  };

  const writeInFieldNames = useMemo(() => {
    if (surveyData && surveyData.data) {
      const writeIns = surveyData.data.filter((element) => element.type === 'text');

      const writeInIds = writeIns.reduce((acc, field) => {
        return acc.concat(field.cms_ids);
      }, []);

      writeInIds.push('comment');

      return writeInIds;
    }
  }, [surveyData]);

  const filterFilledFormValues = (formValues: any): any => {
    const filledValues: any = {};
    Object.keys(formValues).forEach((key) => {
      if (!!formValues[key] && writeInFieldNames.indexOf(key) === -1) {
        filledValues[key] = formValues[key];
      }
    });
    return filledValues;
  };

  const filterFilledWriteInValues = (formValues: any): any => {
    const filledValues: any = {};
    Object.keys(formValues).forEach((key) => {
      if (!!formValues[key] && writeInFieldNames.indexOf(key) >= 0) {
        filledValues[key] = formValues[key];
      }
    });

    return isEmpty(filledValues) ? null : filledValues;
  };

  const onSubmit = async (formValues: any) => {
    const filledValues = filterFilledFormValues(formValues);
    const filledWriteInValues = filterFilledWriteInValues(formValues);

    await submitSurvey(filledValues, filledWriteInValues).then(() => {
      setUserHasSubmittedFlag(userData, true);
    });
  };

  const getFormSections = (): JSX.Element[] => {
    return surveyData.text.sections.map((section: any, index: number) => {
      const data = {
        section: section,
        data: surveyData.data.filter((element) => element.section === section.id),
        errors: surveyData.text.errors,
      };

      switch (section.type) {
        case 'agree_disagree':
          return <AgreementForm key={index} formRegister={register} formErrors={errors} data={data} />;
        case 'rating':
          return <RatingForm key={index} formRegister={register} formErrors={errors} data={data} />;
      }
    });
  };

  const getSurveyForm = (): JSX.Element => {
    if (!surveyData) return <></>;
    return (
      <>
        <h1 className={css(styles.title)}>{cmsData.headline}</h1>
        <p className={css(styles.description)}>{cmsData.description}</p>
        <form onSubmit={handleSubmit(onSubmit)} className={css(styles.form)}>
          {getFormSections()}
          <CommentForm copy={surveyData.text.comment} formRegister={register} name='comment' />
          <button type='submit' className={css(styles.submit)}>
            {cmsData.submit_button_text}
          </button>
        </form>
      </>
    );
  };

  const getCompletedSurveyMessage = (): JSX.Element => {
    return (
      <div className={css(styles.completionContainer)}>
        <h1>{cmsData.completed_message.headline}</h1>
        <div dangerouslySetInnerHTML={{ __html: cmsData.completed_message.content }}></div>
      </div>
    );
  };

  return (
    <main className={css(styles.main)}>
      <Prompt
        when={!hasSurveyBeenSubmitted && !!userData}
        message={JSON.stringify({
          title: cmsData.incompleted_message.headline,
          content: cmsData.incompleted_message.content,
          leaveButtonText: cmsData.incompleted_message.exit_button_text,
          stayButtonText: cmsData.incompleted_message.continue_button_text,
        })}
      />
      <section className={css(styles.section)}>
        <SharedCarousel />
        {loading ? null : hasSurveyBeenSubmitted ? getCompletedSurveyMessage() : getSurveyForm()}
      </section>
    </main>
  );
};
